import React, { useState } from "react";
import { Settings } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function AccountSettings() {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  if (!user) return null;

  const update = async (patch) => {
    setSaving(true);
    // optimistic
    setUser((u) => ({ ...u, ...patch }));
    try {
      const r = await api.patch("/auth/preferences", patch);
      if (r.data?.user) setUser(r.data.user);
    } catch {
      setUser((u) => ({ ...u, ...Object.fromEntries(Object.keys(patch).map(k => [k, !patch[k]])) }));
      toast.error("Couldn't save that setting");
    } finally { setSaving(false); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="nav-settings-button" title="Account settings">
          <Settings className="w-4 h-4" />
          <span className="hidden lg:inline">Settings</span>
        </button>
      </DialogTrigger>
      <DialogContent data-testid="account-settings-dialog">
        <DialogHeader>
          <DialogTitle>Email notifications</DialogTitle>
          <DialogDescription>Choose which activity emails Loom sends you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-stone-800">New sign-in alerts</div>
              <p className="text-sm text-stone-500">Email me whenever my account is signed into.</p>
            </div>
            <Switch
              checked={user.notify_signin !== false}
              onCheckedChange={(v) => update({ notify_signin: v })}
              disabled={saving}
              data-testid="toggle-notify-signin"
            />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-stone-800">Upload ready</div>
              <p className="text-sm text-stone-500">Email me when a PDF finishes processing into a deck.</p>
            </div>
            <Switch
              checked={user.notify_upload !== false}
              onCheckedChange={(v) => update({ notify_upload: v })}
              disabled={saving}
              data-testid="toggle-notify-upload"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
